import { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { usePanelTokens } from "../../../utils/panelTokens";
import { DOWNLOAD_CATALOG, downloadPath } from "./catalog";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CollectionToolbar, { FilterChips } from "../../Templates/CollectionToolbar";
import CollectionTable from "../../Templates/CollectionTable";

/**
 * Public downloads index — the developer-facing catalog of everything we
 * distribute. Findable logged out; the artifacts themselves gate on sign-in
 * where they have to (see `access` in catalog.js).
 */
/** The card's facts, as columns. */
const DOWNLOAD_COLUMNS = [
  { key: "title", label: "Download" },
  { key: "category", label: "Category" },
  { key: "tagline", label: "What it is" },
  {
    key: "access",
    label: "Access",
    render: (d) => (d.access === "signin" ? "Sign-in required" : "Open"),
  },
];

const PublicDownloads = () => {
  const t = usePanelTokens();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [view, setView] = useState("cards");

  // Every category the catalog actually carries — `category` has been on each
  // entry all along, it was just never wired to anything.
  const categories = useMemo(
    () => [...new Set(DOWNLOAD_CATALOG.map((d) => d.category))].sort(),
    []
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DOWNLOAD_CATALOG.filter((d) => {
      if (category !== "all" && d.category !== category) return false;
      if (!q) return true;
      return [d.title, d.tagline, d.category].filter(Boolean).some((f) => f.toLowerCase().includes(q));
    });
  }, [query, category]);

  return (
    <Box sx={{ background: t.ground, color: t.text, minHeight: "100%" }}>
      <Box sx={{ px: { xs: 3, md: 7 }, pt: { xs: 6, md: 9 }, pb: { xs: 5, md: 7 }, textAlign: "center", borderBottom: `1px solid ${t.border}` }}>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: 34, md: 48 }, fontWeight: 700, letterSpacing: "-0.01em", color: t.textStrong }}>
          Developer Downloads
        </Box>
        <Box component="p" sx={{ mt: 2, mb: 0, mx: "auto", maxWidth: 640, fontSize: { xs: 15, md: 17 }, color: t.textMuted, lineHeight: 1.6 }}>
          Firmware, release bundles and hardware assets for building on the Vecros
          platform. Browse freely — sign in when you're ready to pull a file.
        </Box>
      </Box>

      <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto" }}>
        <Box sx={{ mb: 2.5 }}>
          <CollectionToolbar
            query={query}
            onQueryChange={setQuery}
            placeholder="Search downloads…"
            view={view}
            onViewChange={setView}
          >
            <FilterChips
              value={category}
              onChange={setCategory}
              options={categories.map((c) => ({ value: c, label: c }))}
            />
          </CollectionToolbar>
        </Box>

        {shown.length === 0 && (
          <Box sx={{ py: 6, textAlign: "center", color: t.textMuted, fontSize: 14 }}>
            Nothing matches that. Clear the search or pick another category.
          </Box>
        )}

        {view === "table" && shown.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <CollectionTable
              columns={DOWNLOAD_COLUMNS}
              rows={shown}
              rowKey={(d) => d.slug}
              onRowClick={(d) => navigate(downloadPath(d.slug))}
            />
          </Box>
        )}

        {view === "cards" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 2.5,
            mb: 5,
          }}
        >
          {shown.map((d) => (
            <Box
              key={d.slug}
              component={RouterLink}
              to={downloadPath(d.slug)}
              sx={{
                p: 3,
                borderRadius: 2,
                textDecoration: "none",
                color: "inherit",
                display: "block",
                background: t.surface,
                border: `1px solid ${t.border}`,
                transition: "border-color .15s, transform .15s",
                "&:hover": { borderColor: t.accent, transform: "translateY(-2px)" },
              }}
            >
              <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: t.accent, fontWeight: 700, mb: 1 }}>
                {d.category.toUpperCase()}
              </Box>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 1 }}>{d.title}</Box>
              <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.6, mb: 2, minHeight: 66 }}>{d.tagline}</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: t.accent, fontSize: 13.5, fontWeight: 600 }}>
                  Details
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
                {d.access === "signin" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: 12, color: t.textFaint }}>
                    <LockOutlinedIcon sx={{ fontSize: 13 }} />
                    Sign-in required
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
        )}

        <Box sx={{ mt: 2, p: 3, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surfaceAlt, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>Looking for something else?</Box>
            <Box sx={{ fontSize: 13.5, color: t.textMuted }}>
              Guides, API references and tutorials live in the documentation.
            </Box>
          </Box>
          <Button
            onClick={() => window.open("https://docs.vecros.com/", "_blank", "noopener,noreferrer")}
            variant="outlined"
            sx={{ color: t.accent, borderColor: t.accent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { borderColor: t.accent, background: `${t.accent}14` } }}
          >
            Read the docs
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicDownloads;
